import { computed, type ComputedRef } from 'vue'
import type { Ref } from 'vue'
import { isSameDay } from 'date-fns'

import { useAppAbility } from '@/composition/useAppAbility'
import {
  useResourceDayShared,
  getConflictingTimeEntries,
  type ConflictItem,
  getResourceDayStatus,
} from '@/components/persoplan/PmResourceDay/useResourceDayShared'
import type { ResourceDayVehicleQuery } from '../../../../generated/graphql'

export default function useResourceDayVehicle({
  vehicleId,
  agenda,
  additionalConflictItems,
  startDate,
  endDate,
}: {
  vehicleId: Ref<number | undefined>
  agenda: Ref<ResourceDayVehicleQuery['agenda']>
  additionalConflictItems?: Ref<ConflictItem[] | undefined>
  startDate?: ComputedRef<Date | undefined>
  endDate?: ComputedRef<Date | undefined>
}) {
  const { can } = useAppAbility()

  const userCanCreateOrEditResourceState = computed(() => {
    return can('create', 'ResourceStateVehicle')
  })

  const { normalizedResourceAllocations, normalizedResourceStates } =
    useResourceDayShared({ agenda, userCanCreateOrEditResourceState })

  const normalizedItems = computed(() => {
    const items = [
      ...normalizedResourceAllocations.value,
      ...normalizedResourceStates.value,
    ]

    // Augment overlapping time entries
    items.forEach((item) => {
      item.conflictingItems = getConflictingTimeEntries(
        item,
        items,
        additionalConflictItems?.value
      )
    })

    return items
  })

  const normalizedTitle = computed(() => {
    return agenda.value?.vehicle?.caption
  })

  const resourceDayStatus = computed(() => {
    if (!startDate?.value || !endDate?.value) return
    if (!isSameDay(startDate.value, endDate.value)) {
      // console.log('no resourceDayStatus for multiple days')
      return
    }

    // From here on, we know startDate and endDate are on the same day
    const day = startDate.value

    return getResourceDayStatus({
      items: normalizedItems.value,
      day: day,
    })
  })

  return {
    agenda,
    userCanCreateOrEditResourceState,
    normalizedItems,
    normalizedResourceAllocations,
    normalizedTitle,
    resourceDayStatus,
  }
}
